import React from 'react';
import i18n from 'i18next';
import confirm from 'modules/confirm';
import { getArrayLength } from 'utils';
import { LeadStages } from 'models/Lead';
import { ReleaseOrderSharePreview } from 'router/subrouters/DataRoom/models/DataRoomBulk';

import './ReleaseOrderConfirm.styles.scss';

// Also used by other automation confirms that should share the mobile sheet.
export const RELEASE_ORDER_CONFIRM_CLASS = 'release-order-confirm';

const MAX_NAMED_LEADS = 3;

type LeadNamesProps = {
  names: string[];
  count: number;
};

// "A, B and C", or "A, B, C and N more" past three.
const LeadNames: React.FC<LeadNamesProps> = ({ names, count }) => {
  const namedLeads = names.slice(0, MAX_NAMED_LEADS);
  const lastIndex = getArrayLength(namedLeads) - 1;
  const restCount = count - getArrayLength(namedLeads);

  return (
    <>
      {namedLeads.map((name, index) => (
        <React.Fragment key={index}>
          {index > 0 &&
            (index === lastIndex && restCount <= 0
              ? ` ${i18n.t('and')} `
              : ', ')}
          <b>{name}</b>
        </React.Fragment>
      ))}
      {restCount > 0 && ` ${i18n.t('andCountMoreLeads', { count: restCount })}`}
    </>
  );
};

export const confirmShareEverythingAtOnce = () =>
  confirm({
    className: RELEASE_ORDER_CONFIRM_CLASS,
    title: i18n.t('shareEverythingAtOnce'),
    content: <p>{i18n.t('shareEverythingAtOnceDescription')}</p>,
    submitBtnText: i18n.t('switch'),
  });

export const confirmShareOneAtATime = () =>
  confirm({
    className: RELEASE_ORDER_CONFIRM_CLASS,
    title: i18n.t('shareOneAtATime'),
    content: <p>{i18n.t('shareOneAtATimeDescription')}</p>,
    submitBtnText: i18n.t('switch'),
  });

export const confirmSaveStageChanges = (removedFilesCount: number) =>
  confirm({
    className: RELEASE_ORDER_CONFIRM_CLASS,
    title: i18n.t('saveChangesToThisStage'),
    content: (
      <p>
        {i18n.t('removedSharedFilesDescription', { count: removedFilesCount })}
      </p>
    ),
    submitBtnText: i18n.t('saveChanges'),
  });

type RemoveLeadsFromAutomationConfig = {
  // Only the first three are named.
  leadNames: string[];
  leadsCount: number;
  filesCount: number;
  // Left out when the leads are part-way through orders of different stages.
  stage?: string;
};

export const confirmRemoveLeadsFromAutomation = (
  config: RemoveLeadsFromAutomationConfig,
) => {
  const { leadNames, leadsCount, filesCount, stage } = config;

  return confirm({
    className: RELEASE_ORDER_CONFIRM_CLASS,
    type: 'delete',
    title: i18n.t('removeLeadsFromAutomation', { count: leadsCount }),
    content: (
      <p>
        <LeadNames names={leadNames} count={leadsCount} />{' '}
        {stage
          ? i18n.t('leadsPartWayThroughStageOrder', {
              count: leadsCount,
              stage,
            })
          : i18n.t('leadsPartWayThroughOrder', { count: leadsCount })}{' '}
        {i18n.t('sharingFilesSkipsOrder', { count: filesCount })}{' '}
        {i18n.t('removedFromAutomationConsequences')}
      </p>
    ),
    submitBtnText: i18n.t('shareAndRemove'),
  });
};

// Resolves true when no lead is left waiting on a file the share takes away, or the broker agrees to it.
export const confirmStopLeadsOrder = async (
  preview: ReleaseOrderSharePreview,
  stageCopies: Record<LeadStages, string>,
) => {
  const { stuckLeads = [], stuckLeadsCount, stuckStep } = preview;

  if (!stuckLeadsCount) return true;

  const stage = stuckStep && stageCopies[stuckStep];

  return confirm({
    className: RELEASE_ORDER_CONFIRM_CLASS,
    type: 'delete',
    title: i18n.t('revokeFileInProgress'),
    content: (
      <p>
        <LeadNames
          names={stuckLeads.map(({ name }) => name)}
          count={stuckLeadsCount}
        />{' '}
        {stage
          ? i18n.t('leadsWaitingOnStageFile', { count: stuckLeadsCount, stage })
          : i18n.t('leadsWaitingOnFile', { count: stuckLeadsCount })}{' '}
        {i18n.t('revokingFileStopsOrder')}
      </p>
    ),
    submitBtnText: i18n.t('revokeAccess'),
  });
};
